class FacetFilters {
  constructor(component) {
    this.component = component;
    this.elements = {};
    this.eventHandlers = {
      delegateEvent: this.#delegateEvent.bind(this),
      removeSingleFilter: this.#removeSingleFilter.bind(this),
      removeAllFilters: this.#removeAllFilters.bind(this),
      resetFilterGroup: this.#resetFilterGroup.bind(this),
      toggleOptionsDrawer: this.#toggleOptionsDrawer.bind(this),
      updateFilterGroup: this.#updateFilterGroup.bind(this),
    };
    this.componentId = null;
    this.sectionId = null;
    this.sortActive = null;

    this.#validateRequiredAttributes();
    this.#initialize();
  }

  /* VALIDATION */
  #validateRequiredAttributes() {
    this.componentId = this.component.dataset.componentId;
    this.sectionId = this.component.dataset.sectionId;
    this.sortActive = this.component.dataset.sortActive;

    if (!this.componentId) {
      throw new Error(`FacetFilters[#validateRequiredAttributes]: Component with id ${this.component.id} must have a data-component-id attribute.`);
    }

    if (!this.sectionId) {
      throw new Error(`FacetFilters[#validateRequiredAttributes]: Component with id ${this.component.id} must have a data-section-id attribute.`);
    }

    if (!this.sortActive) {
      throw new Error(`FacetFilters[#validateRequiredAttributes]: Component with id ${this.component.id} must have a data-sort-active attribute.`);
    }
  }

  #validateCachedElements() {
    for (const [key, element] of Object.entries(this.elements)) {
      if (Array.isArray(element)) {
        if (!element.every(el => el instanceof HTMLElement)) {
          throw new Error(`FacetFilters[#validateCachedElements]: Invalid array of elements for [${key}]`);
        }
      } else if (!(element instanceof HTMLElement)) {
        throw new Error(`FacetFilters[#validateCachedElements]: Required element [${key}] not found`);
      }
    }
  }

  /* SIDE EFFECTS */
  #updateURLParams(params, key=null) {
    const url = new URL(window.location);
    const searchParams = url.searchParams;

    if (key) {
      searchParams.delete(key);
    }

    for (const [key, value] of params.entries()) {
      searchParams.append(key, value);
    }

    url.search = searchParams.toString();
    window.history.replaceState({}, '', url);
  }

  #resetForm(form) {
    const inputs = form.querySelectorAll('[facet-filter-option]');

    if (!inputs.length) {
      throw new Error(`FacetFilters[#resetForm]: No inputs found for form with id ${form.id}`);
    }

    inputs.forEach((input) => input.checked = false);
  }

  /* FETCHING & RENDERING */
  async #fetchSectionHTML() {
    const params = new URLSearchParams(window.location.search).toString();
    const sectionUrl = `${window.location.pathname}?section_id=${this.sectionId}&${params}`;

    try {
      const response = await fetch(sectionUrl);
      return await response.text();
    } catch (error) {
      console.error(`FacetFilters[#fetchSectionHTML]: Error fetching section HTML: ${error}`);
    }
  }

  #renderFromFetch(html) {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    const grid = document.getElementById(`facet-results-${this.componentId}`);

    if (!section || !grid) {
      throw new Error(`FacetFilters[#renderFromFetch]: Section or grid not found for component with id ${this.componentId}`);
    }

    const renderedSection = new DOMParser().parseFromString(html, 'text/html');
    const renderedResets = renderedSection.querySelectorAll('[facet-filter-reset]');
    const renderedActive = renderedSection.getElementById(`facet-tags-${this.componentId}`);
    const renderedCount = renderedSection.getElementById(`facet-count-${this.componentId}`);
    const renderedGrid = renderedSection.getElementById(`facet-results-${this.componentId}`);

    this.elements.resets.forEach((reset, index) => {
      reset.href = renderedResets[index].href;
    })

    this.elements.active.innerHTML = renderedActive.innerHTML;
    this.elements.count.innerHTML = renderedCount.innerHTML;
    grid.innerHTML = renderedGrid.innerHTML;
  }

  /* EVENT HANDLING */
  #bindEvents() {
    this.#removeEventListeners();

    this.component.addEventListener('click', this.eventHandlers.delegateEvent);
    this.elements.active.addEventListener('click', this.eventHandlers.delegateEvent);
  }

  #removeEventListeners() {
    this.component.removeEventListener('click', this.eventHandlers.delegateEvent);
    this.elements.active.removeEventListener('click', this.eventHandlers.delegateEvent);
  }

  #delegateEvent(event) {
    const target = event.target
    
    if (target.hasAttribute('facet-filter-tag')) {
      event.preventDefault();
      this.eventHandlers.removeSingleFilter(target);
    } else if (target.hasAttribute('facet-filter-clear')) {
      this.eventHandlers.removeAllFilters();
    } else if (target.hasAttribute('facet-filter-reset')) {
      event.preventDefault();
      this.eventHandlers.resetFilterGroup(target);
    } else if (target.hasAttribute('facet-filter-toggle')) {
      this.eventHandlers.toggleOptionsDrawer(target);
    } else if (target.hasAttribute('facet-filter-option')) {
      this.eventHandlers.updateFilterGroup(target);
    } else {
      return;
    }
  }

  async #removeSingleFilter(target) {
    const searchKey = target.dataset.filterKey;
    const searchValue = target.dataset.filterValue;

    const url = new URL(target.href);
    window.history.replaceState({}, '', url);

    const form = this.elements.forms.find((form) => form.dataset.filterKey === searchKey);

    if (!form) {
      throw new Error(`FacetFilters[#removeSingleFilter]: Form not found for search key ${searchKey}`);
    }

    const inputs = Array.from(form.querySelectorAll('[facet-filter-option]'));
    inputs.find((input) => input.value === searchValue).checked = false;
    
    const html = await this.#fetchSectionHTML();
    this.#renderFromFetch(html);
  }

  async #removeAllFilters() {
    const url = new URL(window.location);
    const keyToKeep = 'sort_by';
    const valueToKeep = url.searchParams.get(keyToKeep);

    if (valueToKeep) {
      const sortParams = new URLSearchParams({ [keyToKeep]: valueToKeep });
      url.search = sortParams.toString();
    } else {
      url.search = '';
    }

    window.history.replaceState({}, '', url);
    this.elements.forms.forEach((form) => this.#resetForm(form));

    const html = await this.#fetchSectionHTML();
    this.#renderFromFetch(html);
  }

  async #resetFilterGroup(target) {
    const url = new URL(target.href);
    window.history.replaceState({}, '', url);

    const item = target.closest('[facet-filter-item]');

    if (!item) {
      throw new Error(`FacetFilters[#resetFilterGroup]: Item not found for target with id ${target.id}`);
    }

    const form = item.querySelector('[facet-filter-form]');
    this.#resetForm(form);

    const html = await this.#fetchSectionHTML();
    this.#renderFromFetch(html);
  }

  #toggleOptionsDrawer(toggle) {
    const item = toggle.closest('[facet-filter-item]');

    if (!item) {
      throw new Error(`FacetFilters[#toggleOptionsDrawer]: Toggle with id ${toggle.id} is not inside a filter set. Filter set must have a [facet-filter-item] attribute.`);
    }

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    const drawer = item.querySelector('[facet-filter-drawer]');
    const icon = item.querySelector('[facet-filter-icon]');

    if (!drawer || !icon) {
      throw new Error(`FacetFilters[#toggleOptionsDrawer]: Drawer or icon not found for toggle with id ${toggle.id}`);
    }
    
    isOpen
      ? (drawer.classList.remove('grid-rows-1'), drawer.classList.add('grid-rows-0'), icon.classList.remove('rotate-90'))
      : (drawer.classList.add('grid-rows-1'), drawer.classList.remove('grid-rows-0'), icon.classList.add('rotate-90'));

    drawer.setAttribute('tabindex', isOpen ? '0' : '-1');
    toggle.setAttribute('aria-expanded', !isOpen);
  }

  async #updateFilterGroup(input) {
    const form = input.closest('[facet-filter-form]');

    if (!form) {
      throw new Error(`FacetFilters[#updateFilterGroup]: Form not found for input with id ${input.id}`);
    }

    const formData = new FormData(form);
    const formParams = new URLSearchParams(formData);
    const filterKey = form.dataset.filterKey;
    this.#updateURLParams(formParams, filterKey);

    const html = await this.#fetchSectionHTML();
    this.#renderFromFetch(html);
  }

  /* CACHING */
  #cacheElements() {
    this.elements = {
      count: document.getElementById(`facet-count-${this.componentId}`),
      active: document.getElementById(`facet-tags-${this.componentId}`),
      resets: Array.from(this.component.querySelectorAll('[facet-filter-reset]')),
      forms: Array.from(this.component.querySelectorAll('[facet-filter-form]')),
    }
  }

  /* INITIALIZATION */
  #initialize() {
    this.#cacheElements();
    this.#validateCachedElements();
    this.#bindEvents();
  }
}

export default FacetFilters;